import type {
  CollectionReference,
  PartialWithFieldValue,
} from 'firebase/firestore'
import {
  collection,
  doc,
  orderBy,
  query,
  serverTimestamp,
  type Firestore,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { collectionSnapshots } from '../../firestore-mobx/stream'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import type { FirestorePeerReviewQuestion } from './schema'
import { schema, writeSchema } from './schema'
import type { StreamInterface } from 'tricklejs/dist/types'
import { convertDocumentSnapshotToModel } from '../../firestore-mobx/stream'
import { SectionAssignmentPeerReviewQuestion } from '../../models/SectionAssignmentPeerReviewQuestion'
import {
  addDocWithError,
  deleteDocWithError,
  getDocsWithError,
  updateDocWithError,
} from '../../firestore-mobx/fetch'

const converter: FirestoreDataConverter<FirestorePeerReviewQuestion> = {
  toFirestore: (data: PartialWithFieldValue<FirestorePeerReviewQuestion>) => {
    writeSchema.partial().parse(data)
    return data
  },
  fromFirestore: (snapshot: QueryDocumentSnapshot) => {
    const data = snapshot.data({ serverTimestamps: 'estimate' })
    return schema.parse(data)
  },
}

export const empty = Object.freeze(
  schema.parse({
    sectionId: '',
    assignmentId: '',
    prQuestion: '',
    prDescription: '',
    prOrder: 0,
    updatedAt: { seconds: 0, nanoseconds: 0 },
  })
)

const getColRef = (
  firestore: Firestore,
  { sectionId, assignmentId }: { sectionId: string; assignmentId: string }
): CollectionReference<FirestorePeerReviewQuestion> => {
  return collection(
    firestore,
    'section',
    sectionId,
    'assignment',
    assignmentId,
    'peer_review_question'
  ).withConverter(converter)
}

export const getPeerReviewQuestionsStream = (
  repository: FirebaseRepository,
  params: { sectionId: string; assignmentId: string }
): StreamInterface<SectionAssignmentPeerReviewQuestion[]> => {
  const ref = getColRef(repository.firestore, params)

  const q = query(ref, orderBy('prOrder', 'asc'))

  return collectionSnapshots(q).map((snapshot) => {
    return snapshot.docs.map((docSnap) =>
      convertDocumentSnapshotToModel(
        repository,
        docSnap,
        SectionAssignmentPeerReviewQuestion
      )
    )
  })
}

export async function setPeerReviewQuestionsForAssignment(
  repository: FirebaseRepository,
  params: {
    sectionId: string
    assignmentId: string
    questions: Array<{
      prQuestion: string
      prDescription: string
      prOrder: number
    }>
  }
) {
  const { firestore } = repository
  const { sectionId, assignmentId, questions } = params
  const colRef = getColRef(firestore, { sectionId, assignmentId })

  const existing = await getDocsWithError(colRef, 'GetPeerReviewQuestionsError')
  await Promise.all(
    existing.docs.map((d) =>
      deleteDocWithError(d.ref, 'DeletePeerReviewQuestionError')
    )
  )

  if (questions.length === 0) return

  await Promise.all(
    questions.map((qItem) => {
      const data: FirestorePeerReviewQuestion = {
        sectionId,
        assignmentId,
        prQuestion: qItem.prQuestion,
        prDescription: qItem.prDescription,
        prOrder: qItem.prOrder,
        updatedAt: serverTimestamp() as unknown as Date, // will be coerced by converter
      }
      return addDocWithError(colRef, data, 'CreatePeerReviewQuestionError')
    })
  )
}

export async function createPeerReviewQuestion(
  repository: FirebaseRepository,
  params: {
    sectionId: string
    assignmentId: string
    prQuestion: string
    prDescription: string
    prOrder: number
  }
) {
  const { sectionId, assignmentId, prDescription, prOrder, prQuestion } = params
  const colRef = getColRef(repository.firestore, { sectionId, assignmentId })
  return await addDocWithError(
    colRef,
    {
      sectionId,
      assignmentId,
      prQuestion,
      prDescription,
      prOrder,
      updatedAt: serverTimestamp() as unknown as Date,
    },
    'CreatePeerReviewQuestionError'
  )
}

export async function updatePeerReviewQuestion(
  repository: FirebaseRepository,
  params: {
    sectionId: string
    assignmentId: string
    id: string
    prQuestion?: string
    prDescription?: string
    prOrder?: number
  }
) {
  const { sectionId, assignmentId, id, ...rest } = params
  const docRef = doc(
    repository.firestore,
    'section',
    sectionId,
    'assignment',
    assignmentId,
    'peer_review_question',
    id
  ).withConverter(converter)
  const payload: Partial<FirestorePeerReviewQuestion> = {
    ...rest,
    updatedAt: serverTimestamp() as unknown as Date,
  }
  writeSchema.partial().parse(payload)
  return await updateDocWithError(
    docRef,
    payload,
    'UpdatePeerReviewQuestionError'
  )
}

export async function deletePeerReviewQuestion(
  repository: FirebaseRepository,
  params: { sectionId: string; assignmentId: string; id: string }
) {
  const { sectionId, assignmentId, id } = params
  const docRef = doc(
    repository.firestore,
    'section',
    sectionId,
    'assignment',
    assignmentId,
    'peer_review_question',
    id
  )
  return await deleteDocWithError(docRef, 'DeletePeerReviewQuestionError')
}
